import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import en from "@/locales/en/publicTrial.json";
import ar from "@/locales/ar/publicTrial.json";
import fr from "@/locales/fr/publicTrial.json";
import {
  listPublicTrialSlots,
  resolvePublicTrialLink,
  submitPublicTrial,
  PublicTrialError,
  type PublicTrialSlot,
  type PublicTrialConfirmation,
} from "@/services/edgeFunctionWrappers/publicTrialsWrapper";

import { PublicTrialSlotPicker } from "@/components/trials/PublicTrialSlotPicker";
import { PublicTrialPersonalFields, type TrialPersonalFields } from "@/components/trials/PublicTrialPersonalFields";
import { PublicTrialMedicalQuestionnaire } from "@/components/trials/PublicTrialMedicalQuestionnaire";
import { buildTrialSafetyDetails, emptyMedicalAnswers, hasMedicalAnswers } from "@/lib/trials/publicTrialQuestionnaire";
import "@/components/trials/publicTrialForm.css";
import { athleteThemeStyle, VB20_ATHLETE_THEME } from "@/lib/athleteTheme";

const PUBLIC_FORM_STYLE = { ...athleteThemeStyle(VB20_ATHLETE_THEME), "--accent-foreground": "var(--primary-foreground)" };

type Lang = "fr" | "en" | "ar";
type Dict = typeof en;

const DICTS: Record<Lang, Dict> = { en, fr: fr as Dict, ar: ar as Dict };
const LANGS: Lang[] = ["fr", "en", "ar"];
const LANG_LABELS: Record<Lang, string> = { fr: "Français", en: "English", ar: "العربية" };
const LEGACY_KSA = "vb20-ksa";

const GOALS = [
  "fat_loss",
  "fitness",
  "performance",
  "toning",
  "posture",
    "wellness",
  "muscle",
  "other",
] as const;

const FREQUENCIES = ["1", "2", "3", "4+"] as const;

function get(dict: Dict, path: string): string {
  const value = path.split(".").reduce<unknown>(
    (acc, key) => (acc && typeof acc === "object" ? (acc as Record<string, unknown>)[key] : undefined),
    dict,
  );
  return typeof value === "string" ? value : path;
}

function interpolate(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return text.replace(/\{\{(\w+)\}\}/g, (_, k: string) => String(vars[k] ?? ""));
}

interface FormState extends TrialPersonalFields {
  phone: string;
  email: string;
  age: string;
  gender: "male" | "female" | "";
  height_cm: string;
  weight_kg: string;
  clothing_size: string;
  main_goal: string;
  main_goal_other: string;
  currently_training: boolean | null;
  training_type: string;
  training_frequency: string;
  last_trained: string;
  weekly_availability: string;
  tried_ems: boolean | null;
  ems_feedback: string;
  has_limitation: boolean | null;
  limitation_details: string;
  motivation: string;
}

const INITIAL: FormState = {
  first_name: "",
  last_name: "",
  emergency_phone: "",
  phone: "",
  email: "",
  age: "",
  gender: "",
  height_cm: "",
  weight_kg: "",
  clothing_size: "",
  main_goal: "",
  main_goal_other: "",
  currently_training: null,
  training_type: "",
  training_frequency: "",
  last_trained: "",
  weekly_availability: "",
  tried_ems: null,
  ems_feedback: "",
  has_limitation: null,
  limitation_details: "",
  motivation: "",
};

export default function TrialBookingPage() {
  const { token = "" } = useParams<{ token: string }>();
  const isLegacyKsa = token === LEGACY_KSA;
  const [lang, setLang] = useState<Lang>(isLegacyKsa ? "en" : "fr");
  const dict = DICTS[lang];
  const rtl = lang === "ar";
  const t = useCallback(
    (path: string, vars?: Record<string, string | number>) => interpolate(get(dict, path), vars),
    [dict],
  );

  const [gymName, setGymName] = useState<string | null>(null);
  const [linkState, setLinkState] = useState<"loading" | "valid" | "invalid">("loading");
  const [form, setForm] = useState<FormState>(INITIAL);
  const [medical, setMedical] = useState(emptyMedicalAnswers);
  const [slots, setSlots] = useState<PublicTrialSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [slotsError, setSlotsError] = useState(false);
  const [selected, setSelected] = useState<PublicTrialSlot | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<PublicTrialConfirmation | null>(null);

  useEffect(() => {
    let active = true;
    setLinkState("loading");
    resolvePublicTrialLink(token)
      .then((gym) => {
        if (!active) return;
        setGymName(gym.name);
        setLinkState("valid");
      })
      .catch(() => active && setLinkState("invalid"));
    return () => {
      active = false;
    };
  }, [token]);

  const brand = isLegacyKsa ? t("brand") : gymName ?? "";

  useEffect(() => {
    if (!brand) return;
    document.title = `${t("title")} | ${brand}`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", `${t("title")} — ${brand}. ${t("subtitle")}`);
  }, [brand, t]);

  const loadSlots = useCallback(async () => {
    setSlotsLoading(true);
    setSlotsError(false);
    try {
      setSlots(await listPublicTrialSlots(token));
    } catch {
      setSlotsError(true);
    } finally {
      setSlotsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (linkState === "valid") loadSlots();
  }, [loadSlots, linkState]);

  const formatDate = useCallback(
    (iso: string) =>
      new Date(`${iso}T00:00:00`).toLocaleDateString(rtl ? "ar-SA" : lang === "fr" ? "fr-FR" : "en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
      }),
    [rtl, lang],
  );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (
      !form.first_name.trim() ||
      !form.last_name.trim() ||
      !form.emergency_phone.trim() ||
      !form.age ||
      !form.gender ||
      !hasMedicalAnswers(medical) ||
      !form.phone.trim() ||
      !form.height_cm ||
      !form.weight_kg ||
      !form.clothing_size ||
      !form.main_goal
    ) {
      setFormError(t("errors.requiredFields"));
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setFormError(t("errors.email"));
      return;
    }
    if (!selected) {
      setFormError(t("errors.slot"));
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitPublicTrial(token, {
        full_name: `${form.first_name.trim()} ${form.last_name.trim()}`,
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        age: form.age ? Number(form.age) : null,
        gender: form.gender || null,
        height_cm: Number(form.height_cm),
        weight_kg: Number(form.weight_kg),
        clothing_size: form.clothing_size,
        main_goal: form.main_goal,
        main_goal_other: form.main_goal_other.trim(),
        currently_training: form.currently_training,
        training_type: form.training_type.trim(),
        training_frequency: form.training_frequency,
        last_trained: form.last_trained.trim(),
        weekly_availability: form.weekly_availability,
        tried_ems: form.tried_ems,
        ems_feedback: form.ems_feedback.trim(),
        has_limitation: Object.values(medical).some(item => item.answer === true),
        limitation_details: buildTrialSafetyDetails(form.emergency_phone, medical),
        motivation: form.motivation.trim(),
        class_id: selected.class_id,
        class_date: selected.class_date,
      });
      setConfirmation(result);
    } catch (err) {
      const code = err instanceof PublicTrialError ? err.code : "SERVER_ERROR";
      if (code === "CLASS_FULL" || code === "SLOT_NOT_FOUND") {
        setFormError(t("errors.full"));
        setSelected(null);
        loadSlots();
      } else if (code === "DUPLICATE") {
        setFormError(t("errors.duplicate"));
      } else if (code === "RATE_LIMITED") {
        setFormError(t("errors.rate"));
      } else {
        setFormError(t("errors.generic"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const LanguageToggle = (
    <div className="flex shrink-0 gap-1" role="group" aria-label={t("language")}>
      {LANGS.map((l) => (
        <Button
          key={l}
          type="button"
          variant={l === lang ? "default" : "outline"}
          size="sm"
          className="px-2"
          aria-pressed={l === lang} onClick={() => setLang(l)}
        >
          {LANG_LABELS[l]}
        </Button>
      ))}
    </div>
  );

  if (linkState !== "valid") {
    return (
      <main style={PUBLIC_FORM_STYLE} dir={rtl ? "rtl" : "ltr"} className="min-h-screen bg-background text-foreground px-4 py-16">
        <div className="mx-auto max-w-lg rounded-lg border bg-card p-8 text-center shadow-sm">
          <div className="mb-4 flex justify-center">{LanguageToggle}</div>
          {linkState === "loading" ? (
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" aria-label={t("loadingGym")} />
          ) : (
            <>
              <h1 className="text-xl font-bold">{t("invalidLink.title")}</h1>
              <p className="mt-3 text-muted-foreground">{t("invalidLink.body")}</p>
            </>
          )}
        </div>
      </main>
    );
  }

  if (confirmation) {
    return (
      <main style={PUBLIC_FORM_STYLE} dir={rtl ? "rtl" : "ltr"} className="min-h-screen bg-background text-foreground px-4 py-16">
        <div className="mx-auto max-w-lg rounded-lg border bg-card p-8 text-center shadow-sm">
          <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
          <h1 className="mt-4 text-2xl font-bold">{t("success.title")}</h1>
          <p className="mt-3 text-muted-foreground">
            {t("success.body", {
              date: formatDate(confirmation.class_date),
              time: (confirmation.time ?? "").slice(0, 5),
            })}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">{t("success.note")}</p>
          <Button
            className="mt-6"
            onClick={() => {
              setConfirmation(null);
              setForm(INITIAL);
              setMedical(emptyMedicalAnswers());
              setSelected(null);
              loadSlots();
            }}
          >
            {t("success.again")}
          </Button>
        </div>
      </main>
    );
  }

  const sectionTitle = (index: number, title: string, hint?: string) => (
    <div className="mb-3 flex items-start gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-bold text-primary-foreground">
        {index}
      </span>
      <div>
        <h2 className="text-lg font-bold">{title}</h2>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>
    </div>
  );

  const chip = (active: boolean) =>
    cn(
      "h-auto min-h-10 rounded-md border px-3 py-2 text-sm whitespace-normal transition-colors",
      active ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted",
    );

  return (
    <main style={PUBLIC_FORM_STYLE} dir={rtl ? "rtl" : "ltr"} className="trial-booking-form min-h-screen bg-background text-foreground px-3 py-5 sm:px-6 sm:py-8">
      <div className="mx-auto max-w-3xl space-y-4">
        <header className="px-1 pb-1 sm:px-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-semibold text-muted-foreground">{brand}</p>
            {LanguageToggle}
          </div>
          <h1 className="text-3xl font-extrabold leading-tight sm:text-4xl">{t("heading")}<br /><span className="text-primary">{t("headingAccent")}</span></h1>
          <p className="mt-2 max-w-xl text-sm leading-snug text-muted-foreground">{t("subtitle")}</p>
        </header>

        <form onSubmit={handleSubmit} className="space-y-2 pb-8">
          <section className="trial-form-section">
            {sectionTitle(1, t("sections.personal"), t("sections.personalHint"))}
            <PublicTrialPersonalFields value={form} onChange={set} t={t} />
          </section>

          {/* 2. Main goal */}
          <section className="trial-form-section">
            {sectionTitle(2, t("sections.goal"), t("sections.goalHint"))}
            <div role="group" aria-label={t("sections.goal")} className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {GOALS.map((g) => (
                <Button
                  key={g}
                  type="button"
                  variant="outline" aria-pressed={form.main_goal === g} className={cn(chip(form.main_goal === g), "justify-start gap-2 text-start text-xs")}
                  onClick={() => set("main_goal", g)}
                >
                  <span aria-hidden="true" className={cn("h-4 w-4 shrink-0 rounded-full border border-current", form.main_goal === g && "bg-primary-foreground")} />
                  {t(`goals.${g}`)}
                </Button>
              ))}
            </div>
            {form.main_goal === "other" ? (
              <Input
                className="mt-3"
                maxLength={200}
                value={form.main_goal_other}
                onChange={(e) => set("main_goal_other", e.target.value)}
                placeholder={t("goals.other")}
              />
            ) : null}
          </section>

          <section className="trial-form-section">
            {sectionTitle(3, t("sections.medical"), t("medical.hint"))}
            <PublicTrialMedicalQuestionnaire value={medical} onChange={(key, answer) => setMedical(previous => ({ ...previous, [key]: answer }))} t={t} />
            <p className="mt-2 text-xs text-muted-foreground">{t("safetyNote")}</p>
          </section>

          {/* 4. Trial session slot */}
          <section className="trial-form-section">
            {sectionTitle(4, `${t("sections.slot")} *`, t("sections.slotHint"))}
            <div className="space-y-4">
              {slotsLoading ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("slots.loading")}
                </p>
              ) : slotsError ? (
                <div className="space-y-2">
                  <p className="text-sm text-destructive">{t("slots.error")}</p>
                  <Button type="button" variant="outline" size="sm" onClick={loadSlots}>
                    {t("slots.retry")}
                  </Button>
                </div>
              ) : (
                <PublicTrialSlotPicker key={token} slots={slots} value={selected} onChange={setSelected} language={lang} t={t} />
              )}
            </div>

            <Label className="mt-6 block">{t("fields.weeklyAvailability")}</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {FREQUENCIES.map((f) => (
                <Button
                  key={f}
                  type="button"
                  variant="outline" aria-pressed={form.weekly_availability === f} className={chip(form.weekly_availability === f)}
                  onClick={() => set("weekly_availability", f)}
                >
                  {f}
                </Button>
              ))}
            </div>
          </section>

          <div className="space-y-2 pt-4">
            {formError ? (
              <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                {formError}
              </p>
            ) : null}
            <Button
              type="submit"
              size="lg"
              className="w-full rounded-md shadow-lg backdrop-blur"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("submitting")}
                </>
              ) : (
                t("submit")
              )}
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}
